import { Navigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { useUserRole, type UserRole } from "@/hooks/useUserRole";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  allowedRoles?: UserRole[];
  children: React.ReactNode;
}

/**
 * Client-side route guard — part of a dual-layer security model.
 *
 * UX Layer (this component): Prevents unauthenticated or unauthorised users
 * from seeing protected pages. Redirects to /sign-in if not logged in, or
 * /dashboard if their role is insufficient.
 *
 * Data Layer (Azure Functions API): Every API endpoint verifies the Clerk JWT
 * and checks role membership. Even if this component were bypassed, the API
 * would reject any unauthorised request.
 */
export const ProtectedRoute = ({ allowedRoles, children }: ProtectedRouteProps) => {
  const { user, isLoaded } = useUser();
  const { roles, loading: rolesLoading } = useUserRole();

  if (!isLoaded || rolesLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/sign-in" replace />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const hasAccess = roles.some((role) => allowedRoles.includes(role));
    if (!hasAccess) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};
